/**
 * Shopify Reorder Functionality
 * Adds items from a previous order to the current cart
 */

class ShopifyReorder {
  constructor() {
    this.init();
  }

  init() {
    // Listen for reorder button clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('reorder-button') || 
          e.target.closest('.reorder-button')) {
        e.preventDefault();
        const button = e.target.classList.contains('reorder-button') 
          ? e.target 
          : e.target.closest('.reorder-button');
        this.handleReorder(button);
      }
    });
  }

  async handleReorder(button) {
    const orderName = button.dataset.orderName;
    const lineItems = button.dataset.lineItems;

    if (!lineItems) {
      this.showMessage('No order data found', 'error');
      return;
    }

    // Disable button and show loading state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding to cart...';
    button.classList.add('loading');

    try {
      // Parse line items
      const items = JSON.parse(lineItems);
      
      // Add items to cart
      const results = await this.addItemsToCart(items);
      
      // Show results
      this.handleResults(results, orderName);
      
      // Redirect to cart after short delay
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);

    } catch (error) {
      console.error('Reorder error:', error);
      this.showMessage('Failed to add items to cart. Please try again.', 'error');
      button.disabled = false;
      button.textContent = originalText;
      button.classList.remove('loading');
    }
  }

  async addItemsToCart(items) {
    const results = {
      success: [],
      failed: []
    };

    // Add items one by one to handle individual failures
    for (const item of items) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: item.variant_id,
            quantity: item.quantity
          })
        });

        if (response.ok) {
          const data = await response.json();
          results.success.push({
            title: item.title,
            quantity: item.quantity
          });
        } else {
          const error = await response.json();
          results.failed.push({
            title: item.title,
            reason: error.description || 'Product unavailable'
          });
        }
      } catch (error) {
        results.failed.push({
          title: item.title,
          reason: 'Failed to add to cart'
        });
      }
    }

    return results;
  }

  handleResults(results, orderName) {
    const successCount = results.success.length;
    const failedCount = results.failed.length;

    if (successCount > 0 && failedCount === 0) {
      this.showMessage(
        `Successfully added ${successCount} item${successCount > 1 ? 's' : ''} from order ${orderName} to your cart!`,
        'success'
      );
    } else if (successCount > 0 && failedCount > 0) {
      let message = `Added ${successCount} item${successCount > 1 ? 's' : ''} to cart. `;
      message += `${failedCount} item${failedCount > 1 ? 's were' : ' was'} unavailable.`;
      this.showMessage(message, 'warning');
    } else {
      this.showMessage('No items could be added to cart. They may be out of stock.', 'error');
    }
  }

  showMessage(message, type = 'info') {
    // Remove any existing messages
    const existing = document.querySelector('.reorder-message');
    if (existing) {
      existing.remove();
    }

    // Create message element
    const messageDiv = document.createElement('div');
    messageDiv.className = `reorder-message reorder-message--${type}`;
    messageDiv.textContent = message;

    // Add to page
    document.body.appendChild(messageDiv);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      messageDiv.classList.add('fade-out');
      setTimeout(() => messageDiv.remove(), 300);
    }, 5000);
  }

  // Alternative method: Clear cart first, then add all items
  async replaceCartWithOrder(items) {
    try {
      // Clear current cart
      await fetch('/cart/clear.js', { method: 'POST' });

      // Add all items at once
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items.map(item => ({
            id: item.variant_id,
            quantity: item.quantity
          }))
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Replace cart error:', error);
      return false;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ShopifyReorder();
  });
} else {
  new ShopifyReorder();
}